"use client"

import * as React from "react"
import * as SheetPrimitive from "@radix-ui/react-dialog"
import { cva, type VariantProps } from "class-variance-authority"
import { X } from "lucide-react"
import { useTranslation } from "react-i18next"

import { cn } from "@/shared/lib/utils"
import { useIsMobile } from "@/shared/hooks/use-mobile"
import { useSwipeToDismiss } from "./useSwipeToDismiss"

const Sheet = SheetPrimitive.Root

const SheetTrigger = SheetPrimitive.Trigger

const SheetClose = SheetPrimitive.Close

const SheetPortal = SheetPrimitive.Portal

const SheetOverlay = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Overlay
    className={cn(
      "fixed inset-0 z-50 bg-black/50  data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props}
    ref={ref}
  />
))
SheetOverlay.displayName = SheetPrimitive.Overlay.displayName

/** Applied to panel AND backdrop when `instantClose` is set — see SheetContentProps. */
const NO_EXIT_ANIMATION = "data-[state=closed]:!animate-none data-[state=closed]:!duration-0 !transition-none"

const sheetVariants = cva(
  "fixed z-50 gap-4 bg-background p-6 shadow-lg transition ease-in-out data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:duration-300 data-[state=open]:duration-500",
  {
    variants: {
      side: {
        top: "inset-x-0 top-0 border-b data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top",
        bottom:
          "inset-x-0 bottom-0 border-t data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom",
        left: "inset-y-0 left-0 h-full w-[85vw] border-r data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left sm:max-w-sm",
        right:
          "inset-y-0 right-0 h-full w-[85vw] border-l data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right sm:max-w-sm",
      },
    },
    defaultVariants: {
      side: "right",
    },
  }
)

interface SheetContentProps
  extends React.ComponentPropsWithoutRef<typeof SheetPrimitive.Content>,
    VariantProps<typeof sheetVariants> {
  /**
   * Close with NO exit animation, on BOTH the panel and its backdrop.
   *
   * For the case where something else is about to happen that must not capture a
   * half-dismissed sheet — the mobile nav drawer navigating away, where iOS
   * screenshots the outgoing frame and replays it on the swipe back (R9).
   *
   * Covers the overlay deliberately. A first pass set animate-none through
   * `className`, which reaches Content only, so the backdrop went on fading out
   * on its own timeline and the "drawer-free" frame still had a black scrim over
   * it. Both or neither.
   */
  instantClose?: boolean
  /**
   * Mark the sheet as having unsaved changes. When set, EVERY dismissal route —
   * swiping past the threshold, clicking the X, pressing Escape, tapping the
   * backdrop — calls `onDirtyDismissAttempt` instead of closing. The host is
   * responsible for showing a confirmation prompt and then calling
   * `onOpenChange(false)` if the user confirms.
   *
   * This used to be a lie: the doc claimed X and Escape were intercepted while
   * the code guarded the swipe path only, so a dirty sheet closed silently on
   * an X tap (RB-01 F5).
   */
  isDirty?: boolean
  /** Called when dismissal is triggered while `isDirty` is true. */
  onDirtyDismissAttempt?: () => void
  /**
   * Whether the panel may move focus to its first focusable element on open.
   *
   * Defaults to desktop-only. On a phone the first focusable in a form sheet is
   * a text input, and focusing it raises the on-screen keyboard over the panel
   * before the user has decided to type — the sheet opens half-covered and the
   * user's first action is to dismiss a keyboard they never asked for (F3).
   * With a hardware keyboard the same focus is a convenience, so desktop keeps it.
   *
   * Pass `true` explicitly for the few overlays whose SOLE purpose is typing —
   * search overlays / command palettes, PIN entry, MFA codes. Those may focus on
   * mobile too. Pass `false` to suppress it everywhere.
   */
  autoFocusFirstField?: boolean
}

/** Prevent Radix from closing when the click lands on a portaled sub-dropdown (e.g. mention autocomplete). */
function handlePointerDownOutside(e: Event) {
  const target = e.target as HTMLElement | null
  if (target?.closest?.('[data-mention-dropdown]')) {
    e.preventDefault()
  }
}


const SheetContent = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Content>,
  SheetContentProps
>(({
  side = "right",
  className,
  instantClose = false,
  children,
  onPointerDownOutside,
  onEscapeKeyDown,
  onInteractOutside,
  onOpenAutoFocus,
  isDirty = false,
  onDirtyDismissAttempt,
  autoFocusFirstField,
  style,
  ...props
}, ref) => {
  const { t } = useTranslation()
  const isMobile = useIsMobile()
  const shouldAutoFocus = autoFocusFirstField ?? !isMobile

  // closeRef lets the swipe handler trigger Radix's close animation correctly
  const closeRef = React.useRef<HTMLButtonElement>(null)
  const contentRef = React.useRef<HTMLDivElement | null>(null)
  const handleSwipeClose = React.useCallback(() => {
    closeRef.current?.click()
  }, [])

  const { onTouchStart, onTouchMove, onTouchEnd, transform, transition, attachNode } = useSwipeToDismiss(
    side, handleSwipeClose, isDirty, onDirtyDismissAttempt,
  )

  /**
   * Every non-swipe dismissal route funnels through here.
   *
   * Radix's `composeEventHandlers` skips its own handler when the event has been
   * default-prevented, which is what stops the close for all three: the X button's
   * click, Escape, and an outside interaction. Returns true when it swallowed the
   * dismissal, so callers can skip their own follow-up.
   */
  const guardDismiss = React.useCallback((e: { preventDefault: () => void }): boolean => {
    if (!isDirty || !onDirtyDismissAttempt) return false
    e.preventDefault()
    onDirtyDismissAttempt()
    return true
  }, [isDirty, onDirtyDismissAttempt])

  /**
   * iOS keyboard-dismiss gap (IN-336 e). When a text field low in a bottom sheet is
   * focused, Safari scrolls the layout viewport up to clear the keyboard; when the
   * keyboard goes away it does not always scroll back, so the sheet (fixed to the
   * layout viewport) stays shifted and a dark band is left under its footer. The
   * body is scroll-locked by Radix, so nothing in the page corrects it. Put the
   * viewport back when a field inside the sheet loses focus or the visual viewport
   * regains its height. Phones only, bottom sheets only — a static viewport (and
   * Playwright's WebKit, which has no on-screen keyboard) never takes this path.
   */
  // The node arrives in a later commit than the first effect run (Radix's Portal mounts
  // its container in a layout effect), so the effect keys on the node itself, not the ref.
  const [contentNode, setContentNode] = React.useState<HTMLDivElement | null>(null)
  React.useEffect(() => {
    if (!isMobile || side !== 'bottom') return
    const node = contentNode
    if (!node) return
    const vv = window.visualViewport
    let timer: number | undefined
    const settle = () => {
      window.clearTimeout(timer)
      timer = window.setTimeout(() => {
        const shifted = window.scrollY > 0 || (vv?.offsetTop ?? 0) > 0 || (vv?.pageTop ?? 0) > 0
        if (shifted) window.scrollTo(0, 0)
      }, 60)
    }
    const onFocusOut = (e: FocusEvent) => {
      const t = e.target as HTMLElement | null
      if (t && t.matches?.('input, textarea, select, [contenteditable="true"]')) settle()
    }
    node.addEventListener('focusout', onFocusOut)
    vv?.addEventListener('resize', settle)
    return () => {
      window.clearTimeout(timer)
      node.removeEventListener('focusout', onFocusOut)
      vv?.removeEventListener('resize', settle)
    }
  }, [isMobile, side, contentNode])

  // The hook needs the node to watch its data-state; callers may also pass a ref.
  const setRefs = React.useCallback((node: HTMLDivElement | null) => {
    attachNode(node)
    contentRef.current = node
    setContentNode(node)
    if (typeof ref === 'function') ref(node)
    else if (ref) (ref as React.MutableRefObject<HTMLDivElement | null>).current = node
  }, [ref, attachNode])

  return (
    <SheetPortal>
      <SheetOverlay className={cn(instantClose && NO_EXIT_ANIMATION)} />
      <SheetPrimitive.Content
        ref={setRefs}
        // Focus has to be able to land on the panel itself when the first-field
        // focus is suppressed, or it stays on the trigger behind the backdrop and
        // the focus trap has nothing to trap.
        tabIndex={-1}
        className={cn(sheetVariants({ side }), instantClose && NO_EXIT_ANIMATION, className)}
        style={{ ...style, ...(transform ? { transform } : {}), ...(transition ? { transition } : {}) }}
        onPointerDownOutside={(e) => {
          handlePointerDownOutside(e)
          onPointerDownOutside?.(e)
        }}
        onInteractOutside={(e) => {
          guardDismiss(e)
          onInteractOutside?.(e)
        }}
        onEscapeKeyDown={(e) => {
          guardDismiss(e)
          onEscapeKeyDown?.(e)
        }}
        onOpenAutoFocus={(e) => {
          if (!shouldAutoFocus) {
            e.preventDefault()
            // Park focus on the panel instead — synchronously, off the ref rather
            // than e.currentTarget, which the DOM nulls once dispatch returns.
            contentRef.current?.focus()
          }
          onOpenAutoFocus?.(e)
        }}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        {...props}
      >
        {/* Drag handle — only for bottom sheets (standard iOS sheet indicator) */}
        {side === 'bottom' && (
          <div
            aria-hidden
            className="absolute top-2 left-1/2 -translate-x-1/2 h-1 w-10 rounded-full bg-muted-foreground/30 shrink-0"
          />
        )}
        {children}
        {/* 44×44 pt touch target on a phone: p-3.5 adds 14 px on each side around the 16 px icon (p-3 was only 40) */}
        <SheetPrimitive.Close
          ref={closeRef}
          // The swipe path routes a CLEAN dismiss through this same button, and by
          // then isDirty is false, so the guard can't swallow its own close.
          onClick={guardDismiss}
          className="absolute right-1 top-1 p-3 max-sm:p-3.5 rounded-md opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-secondary"
        >
          <X className="h-4 w-4" />
          <span className="sr-only">{t('common:button.close')}</span>
        </SheetPrimitive.Close>
      </SheetPrimitive.Content>
    </SheetPortal>
  )
})
SheetContent.displayName = SheetPrimitive.Content.displayName

const SheetHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col space-y-2 text-center sm:text-left",
      className
    )}
    {...props}
  />
)
SheetHeader.displayName = "SheetHeader"

const SheetFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
      className
    )}
    {...props}
  />
)
SheetFooter.displayName = "SheetFooter"

const SheetTitle = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Title>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold text-foreground", className)}
    {...props}
  />
))
SheetTitle.displayName = SheetPrimitive.Title.displayName

const SheetDescription = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Description>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
))
SheetDescription.displayName = SheetPrimitive.Description.displayName

export {
  Sheet,
  SheetPortal,
  SheetOverlay,
  SheetTrigger,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetFooter,
  SheetTitle,
  SheetDescription,
}
